import type { LessonProgress, TypingAttempt, QuizResult } from './types';

const DB_NAME = 'momlingo';
const DB_VERSION = 1;

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('progress')) {
        db.createObjectStore('progress', { keyPath: 'lessonId' });
      }
      if (!db.objectStoreNames.contains('typingAttempts')) {
        const store = db.createObjectStore('typingAttempts', { autoIncrement: true });
        store.createIndex('lessonId', 'lessonId');
      }
      if (!db.objectStoreNames.contains('quizResults')) {
        const store = db.createObjectStore('quizResults', { autoIncrement: true });
        store.createIndex('lessonId', 'lessonId');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(
  storeName: string,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    const req = fn(store);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveProgress(p: LessonProgress): Promise<void> {
  await tx('progress', 'readwrite', (s) => s.put(p));
}

export async function getProgress(lessonId: string): Promise<LessonProgress | undefined> {
  return tx('progress', 'readonly', (s) => s.get(lessonId));
}

export async function getAllProgress(): Promise<LessonProgress[]> {
  return tx('progress', 'readonly', (s) => s.getAll());
}

export async function saveTypingAttempt(a: TypingAttempt): Promise<void> {
  await tx('typingAttempts', 'readwrite', (s) => s.add(a));
}

export async function getTypingAttempts(lessonId: string): Promise<TypingAttempt[]> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('typingAttempts', 'readonly');
    const store = transaction.objectStore('typingAttempts');
    const index = store.index('lessonId');
    const req = index.getAll(lessonId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function getAllTypingAttempts(): Promise<TypingAttempt[]> {
  return tx('typingAttempts', 'readonly', (s) => s.getAll());
}

export async function saveQuizResult(r: QuizResult): Promise<void> {
  await tx('quizResults', 'readwrite', (s) => s.add(r));
}

export async function getQuizResults(lessonId: string): Promise<QuizResult[]> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('quizResults', 'readonly');
    const store = transaction.objectStore('quizResults');
    const index = store.index('lessonId');
    const req = index.getAll(lessonId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
